/**
 * Comment domain helpers.
 * Pure functions with no external dependencies.
 */

export const CommentType = Object.freeze({
  STANDALONE: 'standalone',
  VERIFICATION: 'verification',
  DEFERRAL: 'deferral',
  CORRESPONDENCE: 'correspondence',
  LICENSE_EDIT: 'license_edit'
});

export function isValidCommentType(type) {
  return Object.values(CommentType).includes(type);
}

export function normalizeCommentType(type) {
  return isValidCommentType(type) ? type : CommentType.STANDALONE;
}

function buildCommentData({
  message,
  type,
  metadata,
  createdBy = null,
  createdAt = new Date()
}) {
  return {
    message: message || '',
    type,
    metadata,
    createdBy: createdBy || null,
    createdAt
  };
}

function assertNullableString(value, fieldName) {
  if (value !== null && typeof value !== 'string') {
    throw new TypeError(`${fieldName} must be a string or null`);
  }
}

function assertNullableDate(value, fieldName) {
  if (value !== null && !(value instanceof Date)) {
    throw new TypeError(`${fieldName} must be a Date or null`);
  }
}

export function buildStandaloneComment({
  message,
  createdBy = null,
  createdAt = new Date()
} = {}) {
  return buildCommentData({
    message,
    type: CommentType.STANDALONE,
    metadata: {},
    createdBy,
    createdAt
  });
}

export function buildVerificationComment({
  message,
  propertyId,
  propertySnapshot = null,
  createdBy = null,
  createdAt = new Date()
} = {}) {
  if (propertyId === undefined) {
    throw new TypeError('propertyId is required');
  }
  assertNullableString(propertyId, 'propertyId');

  return buildCommentData({
    message,
    type: CommentType.VERIFICATION,
    metadata: {
      propertyId,
      propertySnapshot
    },
    createdBy,
    createdAt
  });
}

export function buildDeferralComment({
  message,
  deferred,
  expiresOn,
  createdBy = null,
  createdAt = new Date()
} = {}) {
  if (typeof deferred !== 'boolean') {
    throw new TypeError('deferred must be a boolean');
  }
  if (expiresOn === undefined) {
    throw new TypeError('expiresOn is required');
  }
  assertNullableDate(expiresOn, 'expiresOn');

  return buildCommentData({
    message,
    type: CommentType.DEFERRAL,
    metadata: {
      deferred,
      expiresOn
    },
    createdBy,
    createdAt
  });
}

function assertStringArray(value, fieldName) {
  if (!Array.isArray(value)) {
    throw new TypeError(`${fieldName} must be an array`);
  }

  value.forEach((item, index) => {
    if (typeof item !== 'string') {
      throw new TypeError(`${fieldName}[${index}] must be a string`);
    }
  });
}

export function buildLicenseEditComment({
  message,
  updatedFields,
  createdBy = null,
  createdAt = new Date()
} = {}) {
  if (updatedFields === undefined) {
    throw new TypeError('updatedFields is required');
  }
  assertStringArray(updatedFields, 'updatedFields');

  return buildCommentData({
    message,
    type: CommentType.LICENSE_EDIT,
    metadata: {
      updatedFields
    },
    createdBy,
    createdAt
  });
}
